import { motion } from 'motion/react';
import { rankLabel, rankOf, suitOf, type CardId } from '@basgan/rules';

const SUIT_GLYPH = { D: '♦', C: '♣', H: '♥', S: '♠' } as const;

export type Direction = 'top' | 'left' | 'right';

/**
 * Rakip kartının masaya uçarak geldiği başlangıç noktası — yığının
 * merkezine göre piksel cinsinden sapma.
 *
 * Kendi kartım `layoutId` ile elimden uçuyor, kaynağı ekranda var. Rakibin
 * kartının kaynağı yok (sırtlar isimsiz), bu yüzden oturduğu yerden geliyormuş
 * gibi giriş yaptırıyoruz. Sapma SABİT VERİLEMEZ: masa ekrana göre büyüyüp
 * küçülüyor, sabit değerde kart oyuncunun yerinden değil ortadan doğuyor.
 * Bu yüzden `Table` oyun alanını ölçüp buraya geçiriyor.
 */
export interface Entry {
  x: number;
  y: number;
  rotate: number;
}

interface Props {
  card: CardId;
  selected?: boolean | undefined;
  /** true = yukarı kalkar (atılabilir). undefined = vurgulama uygulanmıyor. */
  playable?: boolean | undefined;
  onClick?: (() => void) | undefined;
  /** Dolu ise kart bu noktadan uçarak masaya iner (rakip atışı). */
  from?: Entry | undefined;
  /**
   * Elde yeni bir rütbe grubunun ilk kartı — soluna ayırıcı boşluk girer.
   *
   * Kartlar eşit aralıkla üst üste binince üç Q ile iki K tek bir blok gibi
   * görünüyor ve yanlış karta basılıyordu. Ayırıcı boşluk grupları gözle
   * sayılabilir hale getiriyor.
   */
  groupStart?: boolean | undefined;
}

/**
 * Kart yüzü: değer SOL ÜST KÖŞEDE.
 *
 * Ortada olduğunda kartlar üst üste binince değerler kayboluyordu. Gerçek
 * iskambil kartları bu yüzden köşe indeksli — elde yelpaze yapıldığında
 * yalnızca sol kenar görünür. Sağ altta 180° dönmüş ikinci indeks, kart
 * masada tek başına dururken gerçek kart hissini veriyor.
 *
 * Ortada büyük soluk bir sembol denendi ve kaldırıldı: elde kartlar üst üste
 * binince alttan taşıp okunmayan gölgeler bırakıyordu.
 */
function Face({ card }: { card: CardId }) {
  const suit = suitOf(card);
  const label = rankLabel(rankOf(card));
  const glyph = SUIT_GLYPH[suit];
  return (
    <>
      <span className="card__index">
        <b>{label}</b>
        <i>{glyph}</i>
      </span>
      <span className="card__index card__index--flip" aria-hidden>
        <b>{label}</b>
        <i>{glyph}</i>
      </span>
    </>
  );
}

export function Card({ card, selected, playable, onClick, from, groupStart }: Props) {
  const suit = suitOf(card);
  const red = suit === 'H' || suit === 'D';
  // Oynanamayan kartı soluklaştırmak denendi ve bırakıldı: el yarı yarıya
  // sönük görünüyordu. Oynanabilirlik yalnızca kartın YUKARI KALKMASIYLA
  // gösteriliyor — olumlu sinyal, olumsuz değil.
  const className = [
    'card',
    red ? 'card--red' : 'card--black',
    selected ? 'card--selected' : '',
    groupStart ? 'card--group-start' : '',
  ].join(' ');

  if (from) {
    return (
      <motion.div
        className={className}
        initial={{ ...from, scale: 0.62, opacity: 0 }}
        animate={{ x: 0, y: 0, rotate: 0, scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 24, mass: 0.7 }}
      >
        <Face card={card} />
      </motion.div>
    );
  }

  /**
   * `layoutId` kritik: kart elden masaya geçtiğinde farklı bir konteynerde
   * render ediliyor ama kimliği aynı kaldığı için Motion aradaki uçuşu kendisi
   * hesaplıyor. Elle koordinat hesabı yok.
   */
  return (
    <motion.button
      layoutId={`card-${card}`}
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={className}
      /* Atılabilir kartlar yukarı kalkar — "bunlar oynanabilir" demenin
         soluklaştırmaktan doğrudan yolu. */
      animate={{ y: selected ? -30 : playable === true ? -16 : 0 }}
      transition={{ type: 'spring', stiffness: 520, damping: 34 }}
      {...(onClick ? { whileTap: { scale: 0.96 } } : {})}
    >
      <Face card={card} />
    </motion.button>
  );
}

/**
 * Kartın küçük, metin içinde kullanılabilir hâli — değişim panelinde
 * "ne aldın / ne verdin" listesi için. Oynanabilir değil, yalnızca gösterge.
 */
export function CardChip({ card, big }: { card: CardId; big?: boolean }) {
  const suit = suitOf(card);
  const red = suit === 'H' || suit === 'D';
  return (
    <span
      className={[
        'chip-card',
        red ? 'chip-card--red' : 'chip-card--black',
        big ? 'chip-card--big' : '',
      ].join(' ')}
    >
      {rankLabel(rankOf(card))}
      <i>{SUIT_GLYPH[suit]}</i>
    </span>
  );
}

/**
 * Rakiplerin kapalı kart sırtı — küçük boy.
 * Yanlardaki oyuncular için kart yatık çiziliyor (`side`): sırtın içeriği
 * olmadığı için döndürmek yerine en/boy takas etmek yeterli ve düzeni bozmuyor.
 */
export function CardBack({ index, side }: { index: number; side?: boolean }) {
  return (
    <motion.div
      className={`card card--back card--mini ${side ? 'card--mini-side' : ''}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.6 }}
      transition={{ delay: Math.min(index * 0.012, 0.2) }}
    />
  );
}
